/**
 * Defense Audio System — Web Audio API sound functions for threat alerts.
 *
 * Named sound functions for each defense event type.
 * Uses playSynthSound() from audioContext.ts for precise Web Audio scheduling
 * (no setTimeout chains — timing is sample-accurate).
 */

import { playSynthSound } from './audioContext';
import { boundaryWarn } from '../infra/boundaryLog';

/** Single ping — threat escalation to caution */
export function playCautionTone(): void {
  boundaryWarn('sound', 'playCautionTone');
  playSynthSound([{ type: 'sine', freq: 600, duration: 0.15, amplitude: 0.3 }]);
}

/** Double ping — threat escalation to danger */
export function playDangerTone(): void {
  boundaryWarn('sound', 'playDangerTone');
  playSynthSound([
    { type: 'sine', freq: 800, duration: 0.1, amplitude: 0.3 },
    { type: 'sine', freq: 800, duration: 0.1, amplitude: 0.3, delay: 0.15 },
  ]);
}

/** Soft relief tone — position exits danger */
export function playReliefTone(): void {
  boundaryWarn('sound', 'playReliefTone');
  playSynthSound([{ type: 'sine', freq: 400, duration: 0.2, amplitude: 0.15 }]);
}

/** Rising sweep — liquidity sweep detected */
export function playSweepTone(): void {
  boundaryWarn('sound', 'playSweepTone');
  playSynthSound([
    { type: 'sine', freq: 400, duration: 0.12, amplitude: 0.25 },
    { type: 'sine', freq: 600, duration: 0.12, amplitude: 0.25, delay: 0.13 },
    { type: 'sine', freq: 900, duration: 0.15, amplitude: 0.3, delay: 0.26 },
  ]);
}

/** Low pulse — exposure limit breached */
export function playExposureBreachTone(): void {
  boundaryWarn('sound', 'playExposureBreachTone');
  playSynthSound([
    { type: 'sine', freq: 300, duration: 0.2, amplitude: 0.35 },
    { type: 'sine', freq: 300, duration: 0.2, amplitude: 0.35, delay: 0.25 },
  ]);
}

/** Descending warble — adverse flow pattern escalation */
export function playAdverseFlowTone(): void {
  boundaryWarn('sound', 'playAdverseFlowTone');
  playSynthSound([
    { type: 'sine', freq: 700, duration: 0.1, amplitude: 0.25 },
    { type: 'sine', freq: 550, duration: 0.1, amplitude: 0.25, delay: 0.12 },
    { type: 'sine', freq: 400, duration: 0.15, amplitude: 0.3, delay: 0.24 },
  ]);
}

/** Ascending two-note chime — order reached front of queue */
export function playQueueFrontTone(): void {
  boundaryWarn('sound', 'playQueueFrontTone');
  playSynthSound([
    { type: 'sine', freq: 1200, duration: 0.12, amplitude: 0.15 },
    { type: 'sine', freq: 1600, duration: 0.15, amplitude: 0.2, delay: 0.12 },
  ]);
}

/** Triple ascending chirp — order at best price */
export function playBestPriceTone(): void {
  boundaryWarn('sound', 'playBestPriceTone');
  playSynthSound([
    { type: 'sine', freq: 800, duration: 0.1, amplitude: 0.12 },
    { type: 'sine', freq: 1000, duration: 0.1, amplitude: 0.12, delay: 0.1 },
    { type: 'sine', freq: 1200, duration: 0.12, amplitude: 0.15, delay: 0.2 },
  ]);
}

/** Boot sound — slow power-up escalation + solid confirmation ding (~1s total)
 *  Phase 1: low rumble building up (150→300Hz, quiet)
 *  Phase 2: mid tone steps up (400Hz, 500Hz)
 *  Phase 3: bright confirmation ding that rings out (800Hz, sustained)
 */
export function playBootTone(): void {
  boundaryWarn('sound', 'playBootTone');
  playSynthSound([
    // Phase 1 — sub-bass rumble, felt more than heard
    { type: 'sine', freq: 150, duration: 0.25, amplitude: 0.1 },
    { type: 'sine', freq: 200, duration: 0.2, amplitude: 0.12, delay: 0.15 },
    { type: 'sine', freq: 300, duration: 0.15, amplitude: 0.15, delay: 0.3 },
    // Phase 2 — mid climb, building momentum
    { type: 'sine', freq: 400, duration: 0.12, amplitude: 0.18, delay: 0.43 },
    { type: 'sine', freq: 500, duration: 0.1, amplitude: 0.2, delay: 0.53 },
    // Phase 3 — the ding: bright, sustained, "locked in"
    { type: 'sine', freq: 800, duration: 0.4, amplitude: 0.3, delay: 0.65 },
  ]);
}
